import React from "react";
import {
  AreaField,
  BilingualField,
  Panel,
  Repeater,
  TextField,
  Toggle,
} from "@/components/studio/Fields";

/* Everything the visitor reads that is not an article. Edited here, stored in
   one document, rendered by the vitrine — so a wording change is a save, not a
   deploy. */

export const ContentPanel = ({ draft, set }) => {
  const field = (path) => (value) => set(path, value);
  const at = (path, fallback = "") =>
    path.split(".").reduce((node, key) => (node || {})[key], draft) ?? fallback;

  return (
    <div className="space-y-6">
      <Panel title="Identité" hint="Le nom et la phrase qui suivent le lecteur de page en page.">
        <BilingualField
          label="Nom du site"
          value={at("brand.name", {})}
          onChange={field("brand.name")}
          testId="brand-name"
        />
        <BilingualField
          label="Signature"
          value={at("brand.tagline", {})}
          onChange={field("brand.tagline")}
          testId="brand-tagline"
        />
      </Panel>

      <Panel title="Accroche" hint="Le premier écran. Le seul que tout le monde lit.">
        <BilingualField
          label="Surtitre"
          value={at("hero.eyebrow", {})}
          onChange={field("hero.eyebrow")}
          testId="hero-eyebrow"
        />
        <BilingualField
          label="Titre"
          hint="Court. Il est composé en très grand."
          value={at("hero.title", {})}
          onChange={field("hero.title")}
          rows={2}
          testId="hero-title"
        />
        <BilingualField
          label="Sous-titre"
          value={at("hero.subtitle", {})}
          onChange={field("hero.subtitle")}
          rows={3}
          testId="hero-subtitle"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BilingualField
            label="Bouton principal"
            value={at("hero.cta_label", {})}
            onChange={field("hero.cta_label")}
            testId="hero-cta"
          />
          <TextField
            label="Lien du bouton principal"
            value={at("hero.cta_href")}
            onChange={field("hero.cta_href")}
            placeholder="/create"
            testId="hero-cta-href"
          />
          <BilingualField
            label="Bouton secondaire"
            hint="Celui-ci charge la démonstration."
            value={at("hero.second_label", {})}
            onChange={field("hero.second_label")}
            testId="hero-second"
          />
        </div>
      </Panel>

      <Panel title="Le journal" hint="Le titre et l’introduction de la page des articles.">
        <BilingualField
          label="Titre"
          value={at("blog.title", {})}
          onChange={field("blog.title")}
          testId="blog-title"
        />
        <BilingualField
          label="Introduction"
          value={at("blog.intro", {})}
          onChange={field("blog.intro")}
          rows={2}
          testId="blog-intro"
        />
      </Panel>

      <Panel
        title="Référencement et partage"
        hint="Ce qui s’affiche dans un onglet, dans Google, et dans l’aperçu quand quelqu’un colle votre lien dans une conversation."
      >
        <BilingualField
          label="Titre de la page"
          value={at("seo.title", {})}
          onChange={field("seo.title")}
          testId="seo-title"
        />
        <BilingualField
          label="Description"
          hint="Moins de 155 caractères : au-delà, Google coupe."
          value={at("seo.description", {})}
          onChange={field("seo.description")}
          rows={2}
          testId="seo-description"
        />
        <TextField
          label="Image de partage"
          hint="1200 × 630 pixels. C’est la vignette de l’aperçu."
          value={at("seo.image_url")}
          onChange={field("seo.image_url")}
          placeholder="https://…"
          testId="seo-image"
        />
      </Panel>

      <Panel title="Réseaux" hint="Laissez vide ce que vous n’utilisez pas : le pied de page n’affiche que ce qui est rempli.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField label="YouTube" value={at("social.youtube")} onChange={field("social.youtube")} testId="social-youtube" />
          <TextField label="Instagram" value={at("social.instagram")} onChange={field("social.instagram")} testId="social-instagram" />
          <TextField label="LinkedIn" value={at("social.linkedin")} onChange={field("social.linkedin")} testId="social-linkedin" />
          <TextField label="X" value={at("social.x")} onChange={field("social.x")} testId="social-x" />
          <TextField label="Courriel" value={at("social.email")} onChange={field("social.email")} testId="social-email" />
        </div>
      </Panel>

      <Panel
        title="Sections libres"
        hint="Des blocs de texte sur l’accueil, entre les arguments et le portfolio. Supprimez-en un et il disparaît du site au prochain enregistrement."
      >
        <Repeater
          items={at("sections", []) || []}
          onChange={field("sections")}
          blank={() => ({ title: { fr: "", en: "" }, body: { fr: "", en: "" }, visible: true })}
          addLabel="Ajouter une section"
          testId="sections"
          renderItem={(item, patch) => (
            <>
              <BilingualField
                label="Titre"
                value={item.title}
                onChange={(value) => patch({ title: value })}
                testId={`section-title-${item.id || "new"}`}
              />
              <BilingualField
                label="Texte"
                value={item.body}
                onChange={(value) => patch({ body: value })}
                rows={4}
                testId={`section-body-${item.id || "new"}`}
              />
              <Toggle
                label="Visible sur le site"
                checked={item.visible !== false}
                onChange={(value) => patch({ visible: value })}
                testId={`section-visible-${item.id || "new"}`}
              />
            </>
          )}
        />
      </Panel>

      <Panel title="Note interne" hint="Pour vous. Jamais affichée sur le site.">
        <AreaField
          label="Bloc-notes"
          value={at("notes")}
          onChange={field("notes")}
          rows={3}
          testId="site-notes"
        />
      </Panel>
    </div>
  );
};

export default ContentPanel;
